import React, { useMemo, useRef, useLayoutEffect } from 'react';
import {
  Button,
  Tooltip,
  IconButton,
  Typography,
  SvgIcon,
  Paper,
  debounce,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  DataTypeProvider,
  SortingState,
  Sorting,
  IntegratedSelection,
  PagingState,
  CustomPaging,
  TableColumnWidthInfo,
  SelectionState,
  EditingState,
} from '@devexpress/dx-react-grid';
import { ReactComponent as SvgSortIcon } from 'assets/sorticon.svg';
import {
  Grid,
  TableHeaderRow,
  Table,
  VirtualTable,
  PagingPanel,
  TableSelection,
  TableFixedColumns,
  TableEditRow,
  TableColumnResizing,
} from '@devexpress/dx-react-grid-material-ui';
import { GROUPS } from 'constants/permissions';
import { Hide } from 'components';
import dayjs from 'dayjs';
import Loading from 'components/atoms/Loading';
import { putGridChemicalPhysical } from 'services/prediction/grid-chemical-physical';
import { useAuth } from 'hooks/useAuth';
import { DataTypeEditors } from 'components/atoms/CustomGridComponents/DataTypeProvider';
import { LABEL_OMEGA_CLASSIFICATION } from 'constants/prediction';
import {
  IRow,
  IGrid,
  IValidationRules,
  ICustomChangeSet,
  ISelectOption,
} from './types';
import useStyles from './style';

const pagingPanelMessages = {
  showAll: 'Todos',
  rowsPerPage: 'Linhas por página',
  info: ({ from, to, count }) => `${from}-${to} de ${count}`,
};

const tableMessages = {
  noData: 'Sem dados disponíveis',
};

const PredictionGridChemicalPhysical = ({
  columns,
  rows,
  dayMonthYearCells = [],
  tooltipCells = [],
  tooltipRecommendationCells = [],
  currentPage,
  pageSize,
  pageSizes,
  loading,
  totalCount,
  getRowById,
  validationRules,
  tooltipMaxStrLength,
  onCurrentPageChange,
  onPageSizeChange,
  onSortingChange,
  getRowId,
  onExport,
  refetch,
}: IGrid) => {
  const classes = useStyles();

  const { checkPermissions } = useAuth();
  const gridRef = useRef<HTMLElement>();
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const hiddenScrollDivRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = React.useState<(string | number)[]>([]);
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [editingRowIds, setEditingRowIds] = React.useState<(string | number)[]>(
    [],
  );
  const [rowChanges, setRowChanges] = React.useState({});
  const [errors, setErrors] = React.useState<IValidationRules>({});
  const [updatingGridData, setUpdatingGridData] =
    React.useState<boolean>(false);

  const [columnWidths, setColumnWidths] = React.useState<
    TableColumnWidthInfo[]
  >(
    columns.map(column => {
      return {
        columnName: column.name,
        width: column.width,
      } as TableColumnWidthInfo;
    }),
  );

  const columnWidthsMemo = useMemo<TableColumnWidthInfo[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          width: column.width,
          wordWrapEnabled: true,
          align: column.align,
        } as TableColumnWidthInfo;
      }),
    [columns],
  );
  const sorting = React.useMemo<Sorting[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          direction: column.direction,
        } as Sorting;
      }),
    [columns],
  );
  const getSelectedRows = React.useMemo(() => {
    return selection.map(rowId => {
      return getRowById(rowId) as IRow;
    });
  }, [getRowById, selection]);

  const canSave = React.useMemo(() => {
    return (
      isEditing &&
      !Object.values(errors).some(rowId =>
        Object.values(rowId).some(row => !row.isValid),
      )
    );
  }, [errors, isEditing]);

  const disableEditingButton = React.useMemo(() => {
    return getSelectedRows.length === 0;
  }, [getSelectedRows]);

  const updateColumnWidth = React.useCallback(colsWidth => {
    setColumnWidths(colsWidth);
  }, []);

  const renderTitleComponent = React.useCallback(
    props => {
      const { children } = props;
      const columnData = columns.find(column => column.title === children);

      if (columnData?.fullTitle) {
        const renderTitle = () => (
          <span className={classes.tooltipLabel}>{columnData?.fullTitle}</span>
        );

        return (
          <TableHeaderRow.Title className={classes.tableTitle} {...props}>
            <Tooltip title={renderTitle()}>
              <span>{children}</span>
            </Tooltip>
          </TableHeaderRow.Title>
        );
      }

      return <TableHeaderRow.Title className={classes.tableTitle} {...props} />;
    },
    [classes.tableTitle, classes.tooltipLabel, columns],
  );

  const TooltipFormatter = React.useCallback(
    ({ value }: { value: string }) => {
      const maxLength = tooltipMaxStrLength || 50;
      let trucatedValue;
      if (value && value.toString().length > maxLength) {
        if (value.toString().length > 50) {
          trucatedValue = `${value.toString().substr(0, 50)}...`;
        } else {
          trucatedValue = `${value.toString().substr(0, maxLength)}...`;
        }

        return (
          <Tooltip
            classes={{ tooltip: classes.tooltipCenterContent }}
            title={<span className={classes.tooltipLabel}>{value}</span>}
          >
            <span>{trucatedValue}</span>
          </Tooltip>
        );
      }

      return value;
    },
    [classes.tooltipCenterContent, classes.tooltipLabel, tooltipMaxStrLength],
  );

  const CellTooltip = React.useCallback(
    props => (
      <DataTypeProvider
        for={tooltipCells}
        formatterComponent={TooltipFormatter}
        editorComponent={TooltipFormatter}
        {...props}
      />
    ),
    [TooltipFormatter, tooltipCells],
  );

  const TooltipRecommendationFormatter = React.useCallback(
    ({ value }: { value: string }) => {
      const maxLength = 50;
      let trucatedValue;
      if (value && value.toString().length > maxLength) {
        if (value.toString().length > 50) {
          trucatedValue = `${value.toString().substr(0, 50)}...`;
        } else {
          trucatedValue = `${value.toString().substr(0, maxLength)}...`;
        }

        return (
          <Tooltip
            classes={{ tooltip: classes.tooltipCenterContent }}
            title={<span className={classes.tooltipLabel}>{value}</span>}
          >
            <span>{trucatedValue}</span>
          </Tooltip>
        );
      }

      return value;
    },
    [classes.tooltipCenterContent, classes.tooltipLabel],
  );

  const CellRecommendationTooltip = React.useCallback(
    props => (
      <DataTypeProvider
        for={tooltipRecommendationCells}
        formatterComponent={TooltipRecommendationFormatter}
        editorComponent={TooltipRecommendationFormatter}
        {...props}
      />
    ),
    [TooltipRecommendationFormatter, tooltipRecommendationCells],
  );

  const DayMonthYearFormatter = React.useCallback(
    ({ value }: { value: string }) => dayjs(value).local().format('DD/MM/YYYY'),
    [],
  );
  const CellDayMonthYear = React.useCallback(
    props => (
      <DataTypeProvider
        for={dayMonthYearCells}
        formatterComponent={DayMonthYearFormatter}
        editorComponent={DayMonthYearFormatter}
        {...props}
      />
    ),
    [DayMonthYearFormatter, dayMonthYearCells],
  );
  const sortColumnsData = React.useMemo(() => {
    const columnsData = {};

    columns.forEach(({ name, sortingEnabled }) => {
      columnsData[name] = sortingEnabled;
    });

    return columnsData;
  }, [columns]);

  const SortingIcon = React.useCallback(
    () => <SvgIcon component={SvgSortIcon} />,
    [],
  );
  const renderSortLabel = React.useCallback(
    ({ children, onSort, column }) => {
      return (
        <>
          {children}
          {sortColumnsData[column.name] && (
            <IconButton
              className={classes.iconButtonContainer}
              onClick={onSort}
            >
              <SortingIcon />
            </IconButton>
          )}
        </>
      );
    },
    [SortingIcon, classes.iconButtonContainer, sortColumnsData],
  );

  const renderTableSelectionRowComponent = React.useCallback(
    props => {
      const { highlighted } = props;

      return (
        <Table.Row
          {...props}
          className={highlighted ? classes.selectedRows : null}
        />
      );
    },
    [classes.selectedRows],
  );

  const Cell = props => {
    const { column, row } = props;
    const cellValue = row[column.name];

    const columnStatus = errors[row.id]?.[column.name];
    const valid = !columnStatus || columnStatus.isValid;
    const title = valid ? '' : errors[row.id][column.name].error;

    if (
      cellValue ||
      column.name === 'omegaClassification' ||
      column.name === 'omegaRecommendation'
    ) {
      return <Table.Cell title={title} {...props} />;
    }
    if (
      column.name === 'tag' ||
      column.name === 'classification' ||
      column.name === 'recommendation' ||
      column.name === 'visualAspect'
    ) {
      return (
        <Table.Cell title={title} {...props}>
          -
        </Table.Cell>
      );
    }

    return (
      <Table.Cell {...props} title={title}>
        0
      </Table.Cell>
    );
  };

  const CellOmegaClassification = React.useCallback(
    props => (
      <DataTypeProvider
        for={['omegaClassification']}
        formatterComponent={({ value }) => {
          return value || '-';
        }}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled) return value;
          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.SelectOptionsEditorComponent({
            options: Object.values(LABEL_OMEGA_CLASSIFICATION).map(item => ({
              label: item,
              value: item,
            })) as ISelectOption[],
            isLoading: false,
            error: hasError,
            value,
            onValueChange,
          });
        }}
        {...props}
      />
    ),
    [errors, getRowId],
  );

  const CellOmegaRecommendation = React.useCallback(
    props => (
      <DataTypeProvider
        for={['omegaRecommendation']}
        formatterComponent={({ value }) => {
          if (!value) return '-';

          return TooltipRecommendationFormatter({ value });
        }}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled) return value;
          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.InputTextEditorComponent({
            maxLength: 500,
            error: hasError,
            value,
            onValueChange,
          });
        }}
        {...props}
      />
    ),
    [TooltipRecommendationFormatter, errors, getRowId],
  );

  const commitChanges = React.useCallback(
    ({ changed }: ICustomChangeSet) => {
      const edited = Object.entries(changed || {}).map(
        ([rowId, rowValues]) => ({
          analysisId: Number(rowId),
          ...rowValues,
          modifiedField: Object.keys(rowValues),
        }),
      );

      setUpdatingGridData(true);
      putGridChemicalPhysical({ analysisPredictionToUpdate: edited })
        .then(() => {
          if (refetch) refetch();
        })
        .finally(() => {
          setUpdatingGridData(false);
        });
    },
    [refetch],
  );

  const editingSelectedColumns = React.useCallback(() => {
    if (isEditing) {
      if (Object.keys(rowChanges).length > 0) {
        commitChanges({ changed: rowChanges });
      }
      setEditingRowIds([]);
      setIsEditing(false);
      setSelection([]);
    } else {
      setEditingRowIds(selection);
      setIsEditing(true);
    }
  }, [commitChanges, isEditing, rowChanges, selection]);

  const RowEditingComponent = React.useCallback(
    props => {
      return <Table.Row {...props} className={classes.selectedRows} />;
    },
    [classes.selectedRows],
  );

  const validate = (editedRows: { [key: string]: IRow }): IValidationRules =>
    Object.entries(editedRows).reduce((acc, [rowId, partialRow]) => {
      const validatedFields = {};
      Object.entries(partialRow).forEach(([colName, colValue]) => {
        if (validationRules && validationRules[colName]) {
          const row = { ...(getRowById(rowId) || ({} as IRow)), ...partialRow };
          validatedFields[colName] = {
            isValid: validationRules[colName].isValid(colValue, row),
            error: validationRules[colName].errorText,
          };
        }
      });

      return {
        ...acc,
        [rowId]: validatedFields,
      };
    }, {});

  const onEdited = debounce(edited => {
    const rowsValidated = validate(edited);

    setErrors(rowsValidated);
  });

  React.useEffect(() => {
    // obtendo todos os ids das linhas da grid
    const rowIds = rows.map(row => getRowId(row));

    /**
     * Atualizo a lista de seleções para corresponder
     * apenas as linhas que estão sendo exibidas.
     */
    setSelection(currentValues => {
      return currentValues.filter(item => rowIds.includes(item));
    });
  }, [getRowId, rows]);

  useLayoutEffect(() => {
    const rootBase = gridRef?.current?.firstChild; // Obtem o elemento RootBase da Grid
    const tableRootContainer = rootBase?.firstChild as HTMLElement; // Container da Tabela para adicionar o evento de scroll horizontal
    const tableOuterDiv = tableRootContainer?.firstChild; // O componente gera uma div pura entre a tabela e o seu container
    const table = tableOuterDiv?.firstChild as HTMLElement; // Elemento da tabela para obter a sua largura

    const hiddenScrollDiv = hiddenScrollDivRef.current as HTMLElement; // Elemento dentro da div externa que possuirá o mesmo tamanho da tabela para scroll externo
    hiddenScrollDiv.style.width = `${table?.offsetWidth + 85}px`; // Atribui a largura da tabela + 85px da Coluna de Checkbox

    const scrollDiv = scrollDivRef.current as HTMLElement; // Elemento onde será colocado o scroll externo

    // Função que será executada ao rolar o scroll externo
    const scrollListener = ({ target }) => {
      const currentScroll = target as HTMLElement;
      tableRootContainer.scrollLeft = currentScroll.scrollLeft; // Scroll da tabela reflete a posição do scroll externo
    };
    scrollDiv.removeEventListener('scroll', scrollListener); // Quando a largura do grid mudar, remover o listener antes de adicionar um novo
    scrollDiv.addEventListener('scroll', scrollListener); // Adicionar o listener para o scroll externo

    return () => scrollDiv.removeEventListener('scroll', scrollListener); // Remover listener ao desmontar o componente
  }, [columnWidths]); // Observar a mudança da largura do grid

  return (
    <div className={classes.root}>
      <div className={classes.summary}>
        <div className={classes.buttonsActionsContainer}>
          <div className={classes.buttonsActionsContainerLeft}>
            <Typography variant="h6">Físico-Química</Typography>
          </div>
          <div className={classes.buttonsActionsContainerRight}>
            <Hide
              hide={
                !checkPermissions([
                  GROUPS.ENGINEERING,
                  GROUPS.PLANNING,
                  GROUPS.ADMINISTRATOR,
                ]) || isEditing
              }
            >
              <Button
                disabled={disableEditingButton}
                onClick={editingSelectedColumns}
                color="primary"
              >
                Editar
              </Button>
            </Hide>
            <Hide
              hide={
                !checkPermissions([
                  GROUPS.ENGINEERING,
                  GROUPS.PLANNING,
                  GROUPS.ADMINISTRATOR,
                ]) || !isEditing
              }
            >
              <Button
                disabled={!canSave}
                onClick={editingSelectedColumns}
                color="primary"
              >
                Salvar
              </Button>
            </Hide>
            <div className={classes.iconButton}>
              <IconButton onClick={onExport}>
                <Tooltip
                  title={
                    <span className={classes.tooltipLabel}>Exportar dados</span>
                  }
                >
                  <GetAppIcon color="primary" />
                </Tooltip>
              </IconButton>
            </div>
          </div>
        </div>
      </div>
      <Paper
        ref={gridRef}
        variant="elevation"
        elevation={0}
        className={classes.container}
      >
        <Grid getRowId={getRowId} columns={columns} rows={rows}>
          <SelectionState
            selection={selection}
            onSelectionChange={setSelection}
          />
          <SortingState sorting={sorting} onSortingChange={onSortingChange} />
          <IntegratedSelection />
          <CellDayMonthYear />
          <CellTooltip />
          <CellRecommendationTooltip />
          <CellOmegaClassification />
          <CellOmegaRecommendation />
          <EditingState
            editingRowIds={editingRowIds}
            rowChanges={rowChanges}
            onRowChangesChange={newChangedRows => {
              onEdited(newChangedRows);
              setRowChanges(newChangedRows);
            }}
            onCommitChanges={commitChanges}
          />
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={onCurrentPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
          <CustomPaging totalCount={totalCount} />
          <VirtualTable
            messages={tableMessages}
            columnExtensions={columnWidthsMemo}
            cellComponent={Cell}
          />

          <TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={updateColumnWidth}
          />
          <TableHeaderRow
            titleComponent={renderTitleComponent}
            sortLabelComponent={renderSortLabel}
            showSortingControls
          />
          <TableSelection
            rowComponent={renderTableSelectionRowComponent}
            highlightRow
            showSelectAll
          />
          <TableEditRow
            rowComponent={RowEditingComponent}
            cellComponent={Cell}
          />
          <TableFixedColumns
            cellComponent={props => (
              <TableFixedColumns.Cell {...props} showRightDivider={false} />
            )}
            leftColumns={[TableSelection.COLUMN_TYPE]}
          />
          <PagingPanel messages={pagingPanelMessages} pageSizes={pageSizes} />
        </Grid>
        <div ref={scrollDivRef} className={classes.scrollDiv}>
          <div ref={hiddenScrollDivRef} className={classes.hiddenScrollDiv} />
        </div>
        {(loading || updatingGridData) && <Loading />}
      </Paper>
    </div>
  );
};

export default PredictionGridChemicalPhysical;
